import { useMemo, useState } from 'react';
import { ApiError, atlas, type CreatedProposalResponse, type FileResponse } from '../lib/api';

/**
 * The revision workspace.
 *
 * Saving appends an immutable proposal; it never overwrites the canonical document.
 * That is what lets the surrounding policy be advisory: a warning can be shown and the
 * work still kept, because nothing was destroyed to produce it.
 *
 * Review admission and protected promotion are separate controls on purpose. Accepting
 * a proposal as reviewed is not the same act as publishing it into canonical source,
 * and a single button would quietly merge the two.
 */

type EditorState =
  | 'clean'
  | 'dirty'
  | 'saving'
  | 'saved'
  | 'conflict'
  | 'review-needed'
  | 'promotion-eligible'
  | 'promotion-denied';

export function RevisionEditor({ file, onClose }: { file: FileResponse; onClose: () => void }) {
  const editBase = file.editBase;
  const [draft, setDraft] = useState(editBase?.content ?? '');
  const [reason, setReason] = useState('');
  const [state, setState] = useState<EditorState>('clean');
  const [result, setResult] = useState<CreatedProposalResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dirty = editBase !== null && draft !== editBase.content;
  const effectiveState: EditorState = state === 'clean' && dirty ? 'dirty' : state;

  const changedLines = useMemo(() => {
    const before = (editBase?.content ?? '').split('\n');
    const after = draft.split('\n');
    let changed = Math.abs(before.length - after.length);
    for (let index = 0; index < Math.min(before.length, after.length); index += 1) {
      if (before[index] !== after[index]) changed += 1;
    }
    return changed;
  }, [draft, editBase]);

  const save = async () => {
    // Without the exact base there is nothing to propose against, and saving would
    // offer whatever the editor happened to start with as a replacement.
    if (!editBase) return;
    setState('saving');
    setError(null);
    try {
      const created = await atlas.createProposal(file.document.sourcePath, {
        content: draft,
        reason: reason.trim() || 'No reason given',
        baseRevision: editBase.revision,
        baseContentDigest: editBase.contentDigest,
      });
      setResult(created);
      setState(
        created.conflict
          ? 'conflict'
          : created.policy.decision === 'review-needed'
            ? 'review-needed'
            : 'saved',
      );
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'The proposal could not be saved.');
      setState('dirty');
    }
  };

  const promote = async () => {
    if (!result) return;
    try {
      const receipt = await atlas.promote(result.proposal.id);
      // A refusal is a recorded outcome, not a silent no-op.
      setState(receipt.receipt.status === 'refused' ? 'promotion-denied' : 'promotion-eligible');
      setError(receipt.receipt.status === 'refused' ? receipt.receipt.reason : null);
    } catch (caught) {
      setState('promotion-denied');
      setError(caught instanceof ApiError ? caught.message : 'Promotion was refused.');
    }
  };

  const revision = result?.revision;

  if (!editBase) {
    return (
      <div className="proposal-banner">
        <div className="proposal-header">
          <span className="proposal-icon" aria-hidden="true">
            🔒
          </span>
          <div className="proposal-title">Editing unavailable</div>
        </div>
        <div style={{ padding: '12px 16px' }}>
          <p className="text-small">
            This document cannot be edited here. Proposing a revision needs the exact current
            source, which is provided only to a caller holding a valid proposal capability.
          </p>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="proposal-banner">
      <div className="proposal-header">
        <span className="proposal-icon" aria-hidden="true">
          📝
        </span>
        <div className="proposal-title">Proposed revision: {file.document.title}</div>
        <span className={`badge ${statusBadge(effectiveState)}`}>{statusLabel(effectiveState)}</span>
      </div>

      {revision ? (
        <div className="proposal-meta">
          <div className="meta-row">
            <span className="meta-label">Principal:</span>
            <span className="principal-chip">{revision.audit.principalId} (Okta)</span>
            <span className="meta-label">Actor:</span>
            <span className="actor-chip">{revision.audit.actorId}</span>
          </div>
          <div className="meta-row">
            <span className="meta-label">Task:</span>
            <code>{revision.audit.taskRef ?? 'none'}</code>
            <span className="meta-label">Base rev:</span>
            <code>{revision.audit.baseRevision}</code>
            <span className="meta-label">Timestamp:</span>
            {revision.audit.timestamp}
          </div>
          {revision.parents.length > 0 ? (
            <div className="meta-row">
              {/* Both parents are shown in full so the divergence is inspectable. */}
              <span className="meta-label" style={{ color: 'var(--status-error)' }}>
                Conflict parents:
              </span>
              {revision.parents.map((parent) => (
                <code key={parent}>{parent}</code>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {result?.policy.signals.length ? (
        <div className="proposal-meta">
          {result.policy.signals.map((signal) => (
            <div className="meta-row" key={signal.code}>
              <span className={signal.severity === 'warning' ? 'warning-text' : 'meta-label'}>
                {signal.severity === 'warning' ? '⚠' : 'ℹ'} {signal.message}
              </span>
              {signal.evidence ? <code>{signal.evidence}</code> : null}
            </div>
          ))}
        </div>
      ) : null}

      <div style={{ padding: '12px 16px' }}>
        <label className="field-label" htmlFor="atlas-proposal-reason">
          Reason for this revision
        </label>
        <input
          id="atlas-proposal-reason"
          className="link-path"
          style={{ width: '100%', marginBottom: 12, padding: 6 }}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why this change is proposed"
        />
        <label className="field-label" htmlFor="atlas-proposal-body">
          Markdown source
        </label>
        <textarea
          id="atlas-proposal-body"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            if (state !== 'saving') setState('clean');
          }}
          spellCheck={false}
          style={{
            width: '100%',
            minHeight: 280,
            fontFamily: 'var(--font-mono)',
            fontSize: 13,
            padding: 12,
            background: 'var(--bg-code)',
            color: 'var(--text-code)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
          }}
        />
        <p className="text-small" style={{ color: 'var(--text-muted)', marginTop: 8 }}>
          {changedLines} changed line{changedLines === 1 ? '' : 's'}. Saving appends an attributable
          revision; the canonical document is never overwritten.
        </p>
        {error ? <p className="error-text">{error}</p> : null}
      </div>

      <div className="proposal-actions">
        <button type="button" className="btn-primary" onClick={save} disabled={!dirty || state === 'saving'}>
          {state === 'saving' ? 'Saving…' : 'Save proposed revision'}
        </button>
        <button
          type="button"
          className="btn-primary"
          style={{ background: '#059669' }}
          onClick={promote}
          // Promotion is enabled only once a proposal exists and review is satisfied.
          disabled={!result || effectiveState === 'review-needed' || effectiveState === 'conflict'}
          title={
            result
              ? 'Publish into protected canonical source'
              : 'Save a proposal before it can be promoted'
          }
        >
          Protected promote
        </button>
        <button type="button" className="btn-secondary" onClick={onClose}>
          Close editor
        </button>
      </div>
    </div>
  );
}

function statusLabel(state: EditorState): string {
  switch (state) {
    case 'dirty':
      return 'Unsaved changes';
    case 'saving':
      return 'Saving';
    case 'saved':
      return 'Proposal saved';
    case 'conflict':
      return 'Conflict — both versions kept';
    case 'review-needed':
      return 'Review needed';
    case 'promotion-eligible':
      return 'Promotion queued';
    case 'promotion-denied':
      return 'Promotion refused';
    default:
      return 'No changes';
  }
}

function statusBadge(state: EditorState): string {
  if (state === 'conflict' || state === 'promotion-denied') return 'status-error';
  if (state === 'review-needed' || state === 'dirty') return 'concept-badge';
  if (state === 'saved' || state === 'promotion-eligible') return 'status-latest';
  return '';
}
